'use client';
import React, { useEffect, useRef } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import SEBIComplianceFooter from './SEBIComplianceFooter';
import MobileBottomNav from './MobileBottomNav';
import { isMarketLive, isPreMarket } from '@/lib/india-market';

interface AppLayoutProps {
  children: React.ReactNode;
  activePath?: string;
}

// Trigger the Upstox market feed edge function every 20 seconds during market hours.
// The edge function opens a WebSocket to Upstox, collects ticks for 25s, then flushes
// them to market_live_data. Supabase Realtime then pushes updates to all connected clients.
function useMarketFeedTrigger() {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    const triggerFeed = async () => {
      if (inFlightRef.current) return; // don't overlap calls
      const now = new Date();
      // Only trigger during live market or pre-market session
      if (!isMarketLive(now) && !isPreMarket(now)) return;

      inFlightRef.current = true;
      try {
        await fetch('/api/market-feed/trigger', { method: 'POST' });
      } catch {
        // Silently ignore — simulation fallback handles UI
      } finally {
        inFlightRef.current = false;
      }
    };

    // Trigger immediately on mount, then every 20 seconds
    triggerFeed();
    timerRef.current = setInterval(triggerFeed, 20000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
}

export default function AppLayout({ children, activePath }: AppLayoutProps) {
  useMarketFeedTrigger();

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar activePath={activePath} />
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        <Topbar />
        <main className="flex-1 overflow-auto scrollbar-thin pb-16 md:pb-0">
          <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 lg:px-6 xl:px-8 2xl:px-10 py-3 lg:py-6">
            {children}
          </div>
          <SEBIComplianceFooter />
        </main>
      </div>
      <MobileBottomNav activePath={activePath} />
    </div>
  );
}